import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { 
  X, 
  Settings, 
  Save, 
  AlertCircle,
  BarChart2,
  LineChart,
  AreaChart,
  PieChart,
  Gauge,
  Filter,
  Layers,
  Table,
} from 'lucide-react';
import axiosInstance from '../../../utils/api';

const CHART_TYPES = [
  { value: 'table', label: 'Table', icon: Table },
  { value: 'bar', label: 'Bar Chart', icon: BarChart2 },
  { value: 'line', label: 'Line Chart', icon: LineChart },
  { value: 'area', label: 'Area Chart', icon: AreaChart },
  { value: 'pie', label: 'Pie Chart', icon: PieChart },
  { value: 'scalar', label: 'Scalar (KPI)', icon: Gauge },
  { value: 'funnel', label: 'Funnel Chart', icon: Filter },
  { value: 'combo', label: 'Combo Chart', icon: Layers },
];

const formatFieldLabel = (field) =>
  field.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());

const ChartSettingsModal = ({ isOpen, onClose, item, chartData, onSave, existingSettings }) => {
  const [formData, setFormData] = useState({
    name: '',
    data_key: '',
    chart_type: 'table',
    mappings: { x: '', y: '', columns: [], column_labels: {} },
    options: { showLegend: false, showTooltip: true, disableFilters: false },
    layout: { x: 0, y: 0, w: 6, h: 4, minW: 1, minH: 1 },
    meta_data: {},
    status: 'active',
  });
  const [availableFields, setAvailableFields] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  // Initialize form with existing settings or item data
  useEffect(() => {
    if (existingSettings) {
      const existingLayout = existingSettings.layout || {};
      const existingMappings = existingSettings.mappings || {};
      setFormData({
        name: existingSettings.name || item?.name || '',
        data_key: existingSettings.data_key || item?.dataKey || '',
        chart_type: existingSettings.chart_type?.toLowerCase() || 'table',
        mappings: {
          x: existingMappings.x || '',
          y: existingMappings.y || '',
          columns: existingMappings.columns || [],
          column_labels: existingMappings.column_labels || {},
        },
        options: {
          showLegend: false,
          showTooltip: true,
          disableFilters: false,
          ...(existingSettings.options || {}),
        },
        layout: {
          x: existingLayout.x ?? 0,
          y: existingLayout.y ?? 0,
          w: existingLayout.w ?? 6,
          h: existingLayout.h ?? 4,
          minW: existingLayout.minW ?? 1,
          minH: existingLayout.minH ?? 1,
        },
        meta_data: existingSettings.meta_data || {},
        status: existingSettings.status || 'active',
      });
    } else if (item) {
      const itemLayout = item.layout || {};
      let itemMappings = { x: '', y: '', columns: [], column_labels: {} };
      try {
        if (item.configuration?.mappings) {
          const parsed =
            typeof item.configuration.mappings === 'string'
              ? JSON.parse(item.configuration.mappings)
              : item.configuration.mappings;
          itemMappings = {
            x: parsed.x || '',
            y: parsed.y || '',
            columns: parsed.columns || [],
            column_labels: parsed.column_labels || {},
          };
        }
      } catch {
        // ignore parse errors
      }

      setFormData((prev) => ({
        ...prev,
        name: item.name || '',
        data_key: item.dataKey || '',
        chart_type: item.configuration?.chart_type?.toLowerCase() || 'table',
        mappings: itemMappings,
      }));
    }
  }, [item, existingSettings]);

  // Extract available fields from chart data
  useEffect(() => {
    if (chartData && chartData.length > 0) {
      const fields = Object.keys(chartData[0]);
      setAvailableFields(fields);

      setFormData((prev) => {
        if (prev.chart_type === 'table' && (!prev.mappings.columns || prev.mappings.columns.length === 0)) {
          const columnLabels = {};
          fields.forEach((field) => {
            columnLabels[field] = formatFieldLabel(field);
          });
          return {
            ...prev,
            mappings: {
              ...prev.mappings,
              columns: fields,
              column_labels: columnLabels,
            },
          };
        }
        return prev;
      });
    }
  }, [chartData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleMappingChange = (key, value) => {
    setFormData((prev) => ({
      ...prev,
      mappings: { ...prev.mappings, [key]: value },
    }));
  };

  const handleOptionChange = (key, value) => {
    setFormData((prev) => ({
      ...prev,
      options: { ...prev.options, [key]: value },
    }));
  };

  const handleColumnToggle = (field) => {
    setFormData((prev) => {
      const columns = prev.mappings.columns || [];
      const columnLabels = { ...(prev.mappings.column_labels || {}) };
      const isSelected = columns.includes(field);

      const nextColumns = isSelected
        ? columns.filter((col) => col !== field)
        : [...columns, field];

      if (!isSelected) {
        columnLabels[field] = columnLabels[field] || formatFieldLabel(field);
      } else {
        delete columnLabels[field];
      }

      return {
        ...prev,
        mappings: {
          ...prev.mappings,
          columns: nextColumns,
          column_labels: columnLabels,
        },
      };
    });
  };

  const handleColumnLabelChange = (field, label) => {
    setFormData((prev) => ({
      ...prev,
      mappings: {
        ...prev.mappings,
        column_labels: {
          ...(prev.mappings.column_labels || {}),
          [field]: label,
        },
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setError(null);

    try {
      const mappings =
        formData.chart_type === 'table'
          ? {
              columns: formData.mappings.columns || [],
              column_labels: formData.mappings.column_labels || {},
            }
          : formData.mappings;

      const payload = {
        dashboard_setting: {
          name: formData.name,
          data_key: formData.data_key,
          chart_type: formData.chart_type,
          mappings,
          options: formData.options,
          layout: {
            ...formData.layout,
            minW: formData.layout.minW || 1,
            minH: formData.layout.minH || 1,
          },
          meta_data: formData.meta_data,
          status: formData.status,
        },
      };

      let response;
      if (existingSettings?.id) {
        response = await axiosInstance.put(`/api/v1/dashboard_settings/${existingSettings.id}`, payload);
      } else {
        response = await axiosInstance.post('/api/v1/dashboard_settings', payload);
      }

      if (onSave) {
        onSave(response.data);
      }
      onClose();
    } catch (err) {
      console.error('Failed to save chart settings:', err);
      setError(err.response?.data?.message || 'Failed to save settings. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const getMappingFields = () => {
    switch (formData.chart_type) {
      case 'table':
        return [];
      case 'pie':
      case 'funnel':
        return ['label', 'value'];
      case 'scalar':
        return ['label'];
      default:
        return ['x', 'y'];
    }
  };

  const isTable = formData.chart_type === 'table';
  const selectedColumns = formData.mappings.columns || [];

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Settings size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Chart Settings</h2>
              <p className="text-sm text-gray-500">Configure how this chart displays data</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertCircle size={18} />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Chart Name
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                placeholder="Enter chart name"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Chart Type
              </label>
              <div className="grid grid-cols-4 gap-2">
                {CHART_TYPES.map((type) => {
                  const IconComponent = type.icon;
                  return (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setFormData((prev) => ({ ...prev, chart_type: type.value }))}
                      className={`flex flex-col items-center gap-1.5 px-3 py-3 text-sm rounded-lg border transition-all ${
                        formData.chart_type === type.value
                          ? 'border-primary bg-primary/10 text-primary font-medium'
                          : 'border-gray-200 hover:border-gray-300 text-gray-600'
                      }`}
                    >
                      <IconComponent size={20} />
                      <span className="text-xs">{type.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {isTable ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Table Columns
                </label>
                <div className="space-y-2 p-4 bg-gray-50 rounded-lg max-h-[240px] overflow-y-auto">
                  {availableFields.length === 0 ? (
                    <p className="text-sm text-gray-500">No fields available from data</p>
                  ) : (
                    availableFields.map((field) => (
                      <div key={field} className="flex items-center gap-3">
                        <label className="flex items-center gap-2 min-w-[140px] cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedColumns.includes(field)}
                            onChange={() => handleColumnToggle(field)}
                            className="w-4 h-4 text-primary rounded border-gray-300 focus:ring-primary"
                          />
                          <span className="text-sm text-gray-700">{formatFieldLabel(field)}</span>
                        </label>
                        {selectedColumns.includes(field) && (
                          <input
                            type="text"
                            value={formData.mappings.column_labels?.[field] || ''}
                            onChange={(e) => handleColumnLabelChange(field, e.target.value)}
                            placeholder="Column label"
                            className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-primary/20 focus:border-primary bg-white"
                          />
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Data Mappings
                </label>
                <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                  {getMappingFields().map((field) => (
                    <div key={field}>
                      <label className="block text-xs font-medium text-gray-600 mb-1 uppercase">
                        {field === 'x' ? 'X-Axis' : field === 'y' ? 'Y-Axis' : field}
                      </label>
                      <select
                        value={formData.mappings[field] || ''}
                        onChange={(e) => handleMappingChange(field, e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white"
                      >
                        <option value="">Select field...</option>
                        {availableFields.map((f) => (
                          <option key={f} value={f}>
                            {formatFieldLabel(f)}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Display Options
              </label>
              <div className="flex flex-wrap gap-6 p-4 bg-gray-50 rounded-lg">
                {isTable ? (
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.options.disableFilters === true}
                      onChange={(e) => handleOptionChange('disableFilters', e.target.checked)}
                      className="w-4 h-4 text-primary rounded border-gray-300 focus:ring-primary"
                    />
                    <span className="text-sm text-gray-700">Disable column filters</span>
                  </label>
                ) : (
                  <>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.options.showLegend}
                        onChange={(e) => handleOptionChange('showLegend', e.target.checked)}
                        className="w-4 h-4 text-primary rounded border-gray-300 focus:ring-primary"
                      />
                      <span className="text-sm text-gray-700">Show Legend</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.options.showTooltip}
                        onChange={(e) => handleOptionChange('showTooltip', e.target.checked)}
                        className="w-4 h-4 text-primary rounded border-gray-300 focus:ring-primary"
                      />
                      <span className="text-sm text-gray-700">Show Tooltip</span>
                    </label>
                  </>
                )}
              </div>
              {isTable && (
                <p className="mt-2 text-xs text-gray-500">
                  Column filters are enabled by default. Users can filter data directly in the table view.
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving || (isTable && selectedColumns.length === 0)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-primary/90 rounded-lg transition-colors disabled:opacity-50"
            >
              <Save size={16} />
              {isSaving ? 'Saving...' : existingSettings?.id ? 'Update Settings' : 'Save Settings'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default ChartSettingsModal;
